import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TID } from "../constants/testIds";
import { Loader2, Shield } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("info.studioaragona@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      nav("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenziali non valide");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-10 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-14 h-14 rounded bg-white p-1 object-contain" />
          <div>
            <div className="font-display font-bold text-lg">Studio Aragona</div>
            <div className="text-xs text-slate-400">Consulenza · Sicurezza sul lavoro</div>
          </div>
        </div>
        <div className="relative z-10">
          <div className="text-sm text-blue-300 font-mono mb-3 tracking-wider">D.LGS. 81/2008 · GESTIONALE DOCUMENTALE</div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight mb-4">
            Documentazione sicurezza,<br /><span className="text-blue-400">sempre sotto controllo.</span>
          </h1>
          <p className="text-slate-300 max-w-md">
            Anagrafica aziende, lavoratori, cantieri e attrezzature. Scadenze monitorate in tempo reale
            e condivisione documenti tramite link protetti.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">© {new Date().getFullYear()} Studio Aragona · Dott. Aragona Adriano</div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-blue-900/40 blur-3xl" />
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo-studio-aragona.png" alt="Studio Aragona" className="w-12 h-12 rounded object-contain" />
            <div>
              <div className="font-display font-bold text-slate-900">Studio Aragona</div>
              <div className="text-xs text-slate-500">SicurDoc</div>
            </div>
          </div>
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-blue-700 bg-blue-50 px-2.5 py-1 rounded mb-4">
              <Shield size={12} /> Accesso riservato
            </div>
            <h2 className="font-display text-3xl font-bold text-slate-900">Bentornato</h2>
            <p className="text-slate-500 mt-1">Entra nella piattaforma per gestire la documentazione sicurezza.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={TID.loginEmail}
                className="mt-1 w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-blue-700 hover:underline">Password dimenticata?</Link>
              </div>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid={TID.loginPassword}
                className="mt-1 w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            </div>
            <button type="submit" disabled={busy} data-testid={TID.loginSubmit}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />}
              Accedi
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />oppure<div className="h-px flex-1 bg-slate-200" /></div>
          <button type="button" data-testid="google-login" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
            className="w-full flex items-center justify-center gap-3 border border-slate-300 hover:bg-slate-50 text-slate-800 font-medium py-2.5 rounded-lg transition">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
            Accedi con Google
          </button>
          <p className="mt-2 text-xs text-slate-400 text-center">Solo per account già registrati dall'amministratore.</p>

          <div className="mt-8 pt-6 border-t border-slate-100 text-xs text-slate-500">
            Piattaforma dedicata allo Studio Aragona · Accesso con account autorizzato.
          </div>
        </div>
      </div>
    </div>
  );
}
